import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { formPaths } from '@shared/constants/form-paths.const';
import { getNatMutControl, getNatMutGroup, natMutCkHelper } from './helpers/natura-mutualistica-form-validators.helper';

export const naturaMutualisticaCoreRules: FormGroupRules = {
    my_DescScopoMutualStat: (form: FormGroup) => {
        descScopoMutualHelper(form, 'my_DescScopoMutualStat', 1);
    },

    my_DescScopoMutual: (form: FormGroup) => {
        descScopoMutualHelper(form, 'my_DescScopoMutual', 0);
    },

    my_CkMutuaDivid: (form: FormGroup): void => {
        ckMutuaHelper(form, 'my_CkMutuaDivid');
    },

    my_CkMutuaRiserve: (form: FormGroup): void => {
        ckMutuaHelper(form, 'my_CkMutuaRiserve');
    },

    my_CkRegolamInterniConformi: (form: FormGroup): void => {
        natMutCkHelper(form, 'my_CkRegolamInterniConformi', 'my_CkRegolamInterni', 1);
    },

    my_StatConfNorm: (form: FormGroup): void => {
        natMutCkHelper(form, 'my_StatConfNorm', 'my_CkStatutoConforme', 0);
    },

    my_AttivEffettSvolta: (form: FormGroup) => {
        natMutCkHelper(form, 'my_AttivEffettSvolta', 'my_CkAttivEffettSvolta', 0, `${formPaths.datiIdentificativi}`);
    },

    my_AspettiOggRegolam: (form: FormGroup) => {
        natMutCkHelper(form, 'my_AspettiOggRegolam', 'my_CkRegolamInterni', 1);
    },

    my_DescParitaSociCoop: (form: FormGroup): void => {
        natMutCkHelper(form, 'my_DescParitaSociCoop', 'my_CkParitaSociCoop', 0);
    },

    my_DescCondizPrevNotaIntegr: (form: FormGroup) => {
        natMutCkHelper(form, 'my_DescCondizPrevNotaIntegr', 'my_CkCondizPrevNotaIntegr', 0);
    },

    my_RegimeDerogatorioProvalenza: (form: FormGroup) => {
        const datiIdentificativiGroup = getGroup(form, `${formPaths.datiIdentificativi}`);
        const naturaMutualisticaGroup = getNatMutGroup(form);
        const my_RegimeDerogatorioProvalenzaControl = getControl(datiIdentificativiGroup, 'my_RegimeDerogatorioProvalenza');
        const my_CkAttPrevalSoci = toNum(getControl(naturaMutualisticaGroup, 'my_CkAttPrevalSoci')?.value);
        const my_CkRientraRegDerogatori = toNum(getControl(naturaMutualisticaGroup, 'my_CkRientraRegDerogatori')?.value);
        const errors = my_RegimeDerogatorioProvalenzaControl.errors ? my_RegimeDerogatorioProvalenzaControl.errors : {};
        const showRequiredError = toNum(my_RegimeDerogatorioProvalenzaControl?.value) === 1 && my_CkAttPrevalSoci === 0 && my_CkRientraRegDerogatori === 1;

        addRemoveError(isNilOrEmpty(my_RegimeDerogatorioProvalenzaControl?.value), errors, 'my_RegimeDerogatorioProvalenza', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(showRequiredError, errors, 'my_RegimeDerogatorioProvalenza', {
            status: REQUIRED_STATUS,
            message: 'Specificare se la cooperativa rientra in uno dei regimi di deroga alla prevalenza.',
        });

        my_RegimeDerogatorioProvalenzaControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkRientraRegDerogatori: (form: FormGroup) => {
        natMutCkHelper(form, 'my_CkRientraRegDerogatori', 'my_CkAttPrevalSoci', 0);
    },

    my_CkAdempiutoProcedureCambioSez: (form: FormGroup) => {
        natMutCkHelper(form, 'my_CkAdempiutoProcedureCambioSez', 'my_CkCambiatoSezione', 1);
    },

    my_CkConsidMutualPrev: (form: FormGroup) => {
        const my_CkConsidMutualPrevControl = getNatMutControl(form, 'my_CkConsidMutualPrev');
        const my_TipoSezEnte = toNum(getControl(form, `${formPaths.datiIdentificativi}.my_TipoSezEnte`)?.value);
        const errors = my_CkConsidMutualPrevControl.errors ? my_CkConsidMutualPrevControl.errors : {};
        const showRequiredError = isNilOrEmpty(my_CkConsidMutualPrevControl?.value);
        const showInconsistentError = toNum(my_CkConsidMutualPrevControl?.value) === 0 && my_TipoSezEnte === 2;

        addRemoveError(showRequiredError, errors, 'my_CkConsidMutualPrev', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(showInconsistentError, errors, 'my_CkConsidMutualPrev', {
            status: 'Cooperativa sociale: selezione incoerente',
        });

        my_CkConsidMutualPrevControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};

const descScopoMutualHelper = (form: FormGroup, targetPath: string, targetCkValue: 0 | 1): void => {
    natMutCkHelper(form, targetPath, 'my_CkScopoMutual', targetCkValue);
};

const ckMutuaHelper = (form: FormGroup, targetPath: string): void => {
    natMutCkHelper(form, targetPath, 'my_CkReqMutualPrev', 0);
};
